import type { ReactNode } from "react";

import { Badge, Card } from "../components/ui";
import { PRODUCTS, productById, type ProductId } from "./registry";

/**
 * How one product's half of a shared surface is drawn.
 *
 * The design puts both products on the same row rather than behind a switcher,
 * and that is the right call: the fact worth surfacing is DIVERGENCE — a
 * merchant sitting on `labeeb:standard` and `mcp:premium` — and a switcher
 * shows one side at a time, which is precisely how a split goes unnoticed for
 * a billing cycle. Everything here therefore renders every product at once.
 *
 * An unwired product's cell says "not wired", never "—" and never blank.
 * Those already mean something specific in this console: "—" is a value the
 * product does not store, and blank is a field that did not load. Reusing
 * either would answer a question the console cannot answer, and the operator
 * would act on it.
 */

/** Which product a surface or panel belongs to. */
export function ProductTag({ id }: { id: ProductId }) {
  const p = productById(id);
  if (!p) return null;
  return (
    <span title={p.declared ? `${p.apiPrefix}/admin/*` : `Not wired — needs ${p.needs}`}>
      <Badge tone={p.declared ? "neutral" : "amber"}>{p.label}</Badge>
    </span>
  );
}

/**
 * The design's "Stored as" cell — one mono line per product, in registry order.
 *
 * FOUR STATES, and they are not interchangeable:
 *   not wired   the platform has no route for this product. Unused today:
 *               both products are declared. A third will hit it first.
 *   not read    `undefined` — the read failed or has not answered. Never
 *               rendered as "no row", which would claim a fact nobody read.
 *   no row      `null` — the product answered and has stored nothing
 *   a value     what is actually stored
 *
 * "no row" is why this cell exists next to a Tier column: the server resolves
 * an absent MCP row AS free, so "free" and "nothing stored" look identical
 * downstream and differ completely in what an operator should do.
 */
export function StoredAs({ stored }: { stored: Partial<Record<ProductId, string | null>> }) {
  return (
    <div className="font-mono text-xs text-slate-500 dark:text-slate-400">
      {PRODUCTS.map((p) => {
        const value = stored[p.id];
        return (
          <div key={p.id}>
            {p.id}:
            {!p.declared ? (
              <span
                className="text-slate-400 italic dark:text-slate-500"
                title={`Not wired — needs ${p.needs}`}
              >
                not wired
              </span>
            ) : value === undefined ? (
              <span className="text-slate-400 italic dark:text-slate-500">not read</span>
            ) : value === null ? (
              <span className="text-slate-400 dark:text-slate-500">no row</span>
            ) : (
              value
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * One product's block on a page cut by product: the Tenant record, Tiers,
 * Settings and Access.
 *
 * The block is the one collapsible unit and the one place the product is
 * named. It starts shut, Labeeb and MCP alike: the page opens as one title per
 * product and the reader expands the one they came for. Every page shares that
 * default; pages choosing their own made Access open and Settings shut.
 * Cards inside it carry no ProductTag and do not fold: a second badge says
 * nothing the block has not, and a fold inside a fold reads as "the first
 * section is all there is".
 *
 * For a product with no route table it draws no body at all — only what would
 * wire it. Nothing of an unwired product can have been read, so any shape in
 * its place would imply a value that does not exist.
 */
export function ProductSection({
  id,
  note,
  children,
}: {
  id: ProductId;
  /** What the block holds, readable while it is shut. */
  note?: ReactNode;
  children: ReactNode;
}) {
  const p = productById(id);
  if (!p) return null;

  return (
    <section data-product-section={id}>
      <Card title={p.label} note={note} aside={<ProductTag id={id} />} collapsible>
        {p.declared ? (
          children
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {p.label} is not wired — needs {p.needs ?? "an admin API"}.
          </p>
        )}
      </Card>
    </section>
  );
}

/**
 * Every product's coverage of one capability, as a compact list.
 *
 * Used where a surface is wholly one product's — Tool caps is MCP's, HITL
 * gates are Labeeb's — so the reader learns the scope of what they are looking
 * at rather than assuming it covers the fleet's whole AI surface.
 */
export function ProductScope({ owns, children }: { owns: ProductId[]; children?: ReactNode }) {
  return (
    // A LINE, not a panel. This is a scope note on the list below it, and a
    // ringed box above every table made the chrome taller than the first row
    // of data on four pages.
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
      <span>Applies to</span>
      {PRODUCTS.map((p) => (
        <span key={p.id} className={owns.includes(p.id) ? "" : "opacity-40"}>
          <ProductTag id={p.id} />
        </span>
      ))}
      {children && <span className="min-w-0">— {children}</span>}
    </p>
  );
}
